import React, { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import '../../styles/SportsTMS.css';

export const NAV = [
  { to: '/platform', label: 'Platform' },
  { to: '/sports', label: 'Sports' },
  { to: '/live-scoring', label: 'Live scoring' },
  { to: '/who-its-for', label: 'Who it’s for' },
];

const SiteLayout = () => {
  const [isStuck, setIsStuck] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { pathname, hash } = useLocation();

  useEffect(() => {
    const onScroll = () => setIsStuck(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // A single-page app keeps the old scroll position across routes, which lands
  // a visitor halfway down a page they have not read yet - so a route change
  // goes to the top, unless the link named a section.
  //
  // Two things this has to handle: a link carrying a hash arrives before the
  // target has been painted, and a link to a section of the page you are
  // already on changes only the hash, which is why `hash` is a dependency.
  useEffect(() => {
    setIsMenuOpen(false);

    if (!hash) {
      window.scrollTo(0, 0);
      return undefined;
    }

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let frame;
    let tries = 0;

    const goToTarget = () => {
      const target = document.getElementById(decodeURIComponent(hash.slice(1)));
      if (target) {
        target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
        return;
      }
      // The page for a new route may not have rendered yet; give it a few frames.
      if (tries++ < 30) frame = requestAnimationFrame(goToTarget);
    };

    frame = requestAnimationFrame(goToTarget);
    return () => cancelAnimationFrame(frame);
  }, [pathname, hash]);

  return (
    <div className="stms">
      <a className="stms-skip" href="#main">Skip to content</a>

      <header className={`stms-head${isStuck ? ' is-stuck' : ''}`}>
        <div className="stms-wrap stms-head__inner">
          <Link className="stms-head__mark" to="/" aria-label="SportsTMS home">
            <img src="/images/sportstms/logo_white.png" alt="SportsTMS" width="176" height="36" />
          </Link>

          <nav className={`stms-nav${isMenuOpen ? ' is-open' : ''}`} aria-label="Main">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                className={({ isActive }) => (isActive ? 'is-current' : undefined)}
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="stms-head__end">
            <Link className="stms-btn stms-btn--solid" to="/demo">Book a demo</Link>
            <button
              className="stms-burger"
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMenuOpen}
              onClick={() => setIsMenuOpen((v) => !v)}
            >
              <span /><span /><span />
            </button>
          </div>
        </div>
      </header>

      <main id="main">
        <Outlet />
      </main>

      <footer className="stms-foot">
        <div className="stms-wrap stms-foot__inner">
          <div className="stms-foot__brand">
            <img src="/images/sportstms/logo_white.png" alt="SportsTMS" width="176" height="36" />
            <p>Tournament management for sports associations, corporate leagues and institutions in India.</p>
          </div>
          <nav className="stms-foot__cols" aria-label="Footer">
            <div>
              <h3>Platform</h3>
              <Link to="/platform">What it covers</Link>
              <Link to="/sports">Sports and scoring</Link>
              <Link to="/live-scoring">Live scoring and boards</Link>
              <Link to="/standings">Standings and records</Link>
            </div>
            <div>
              <h3>Who it’s for</h3>
              <Link to="/who-its-for">Associations and leagues</Link>
              <Link to="/who-its-for#corporate">Corporate leagues</Link>
              <Link to="/who-its-for#roles">The four accounts</Link>
            </div>
            <div>
              <h3>Company</h3>
              <Link to="/demo">Book a demo</Link>
              <Link to="/demo">Contact</Link>
            </div>
          </nav>
        </div>
        <div className="stms-wrap stms-foot__base">
          <p>© {new Date().getFullYear()} SportsTMS</p>
          <p>Tournament Management System</p>
        </div>
      </footer>
    </div>
  );
};

export default SiteLayout;
